import React, { useState, useEffect } from "react";
import {
    Box,
    Button, FormControl,
    Grid, InputLabel, MenuItem,
    Select,
   Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useUpdateJourneyMutation } from "../../store/services/journey-service";
import { useGetSimulationsQuery } from "../../store/services/simulation-service";
import { useGetCoursesQuery } from "../../store/services/university-service";
import { useGetFeatureSwitchesQuery } from "../../store/services/tenancy.service";
import { useSelector } from "react-redux";
import MainCard from "../../components/cards/MainCard";
import JourneyTimeline from "./JourneyTimeline";
import { getCardColor } from "../../utils/colorUtils";
import TrimmedTextField from "../../components/TrimmedTextField";


const JourneyEditForm = ({ initialData, onSave, setSnackbarOpen, setSnackbarMessage }) => {
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    const [UpdateJourney] = useUpdateJourneyMutation();
    const [nameError, setNameError] = useState("");

    // Get tenancy ID and feature switches
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { data: featureSwitchesApiData } = useGetFeatureSwitchesQuery({ tenancyId });
    const featureSwitches = featureSwitchesApiData?.data || featureSwitchesApiData;

    const { data: simData, simLoading, simError } = useGetSimulationsQuery({
        perPage: 200,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: ""
    });

    const { data: courseData, courseLoading, courseError } = useGetCoursesQuery({
        perPage: 200,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: ""
    });

    const [journeyData, setJourneyData] = useState(initialData);

    const [newJourneyOption, setNewJourneyOption] = useState({
        type: 0,
        typeId: "",
        typeName: "", 
        month: "",
        day: "",
    });

    // Determine which options are available based on feature switches
    const simulationsEnabled = featureSwitches?.enableJourneyBuilderSimulations !== false;
    const coursesEnabled = featureSwitches?.enableJourneyBuilderCourses !== false;
    const bothEnabled = simulationsEnabled && coursesEnabled;
    const onlySimulations = simulationsEnabled && !coursesEnabled;
    const onlyCourses = !simulationsEnabled && coursesEnabled;
    const noneEnabled = !simulationsEnabled && !coursesEnabled;

    // Auto-select type when only one option is available
    useEffect(() => {
        if (onlySimulations && newJourneyOption.type !== 0) {
            setNewJourneyOption(prev => ({
                ...prev,
                type: 0,
                typeId: "",
                typeName: ""
            }));
        } else if (onlyCourses && newJourneyOption.type !== 1) {
            setNewJourneyOption(prev => ({
                ...prev,
                type: 1,
                typeId: "",
                typeName: ""
            }));
        }
    }, [onlySimulations, onlyCourses]);


    // because of backend magic for type 0 (sims) we need typeName to be populated but for typeId to be an empty string
    // because of similar magic reasons type 1 (courses) need to have both typeName and typeId populated with the course name
    const isOptionValid = () => {
        return (
            ((newJourneyOption.type === 0 && newJourneyOption.typeName !== "" && newJourneyOption.typeId === "") ||
                (newJourneyOption.type === 1 && newJourneyOption.typeName === newJourneyOption.typeId && newJourneyOption.typeId !== ""))
            && newJourneyOption.month >= 1
            && newJourneyOption.month <= 12
            && newJourneyOption.day >= 1
            && newJourneyOption.day <= 31
        );
    };


    useEffect(() => {
        setJourneyData(initialData);
    }, [initialData]);

    const handleSave = async () => {
        if (!journeyData.name.trim()) {
            setNameError("Journey Name is required");
            return;
        }
        const journeyUpdateCommand = { journeyToUpdate: journeyData };

        await UpdateJourney({ journeyData: journeyUpdateCommand }).unwrap()
        .then((response) => {
            setSnackbarMessage("Journey updated successfully");
            setSnackbarOpen(true);
        })
        .catch((error) => {
            console.log("error", error);
            setSnackbarMessage("Error occurred while updating journey");
            setSnackbarOpen(true);
        });
    };

    const handleAddNewJourneyOption = () => {
        newJourneyOption.dateCreated = new Date().toISOString();
        setJourneyData({ ...journeyData, journeyOptions: [...journeyData.journeyOptions, newJourneyOption] });
        setNewJourneyOption({
            day: "",
            month: "",
            type: 0,
            typeId: "",
            typeName: ""
        });
    };

    const handleRemoveJourneyOption = (index) => {
        const updatedOptions = journeyData.journeyOptions.filter((_, i) => i !== index);
        setJourneyData({ ...journeyData, journeyOptions: updatedOptions });
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
                <TrimmedTextField
                    label="Journey Name"
                    value={journeyData.name ?? journeyData.name}
                    size="medium"
                    sx={{ mb: 4, width: "50%" }}
                    onChange={(e) => {
                        setJourneyData({ ...journeyData, name: e.target.value });
                        if (e.target.value.trim()) setNameError("");
                    }}
                    required
                    error={!!nameError}
                    helperText={nameError}
                />

                <Grid item xs={12}>
                    <MainCard content={true} boxShadow={false} border={false} sx={{ height: '100%', bgcolor: lightMode ? "primary.main" : getCardColor(lightMode) }}>
                        <Typography variant="h5" gutterBottom>
                            Add Simulation or Course
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%', mt: 4, gap: 2 }}>
                            {/* Only show Type dropdown if both options are enabled */}
                            {bothEnabled && (
                                <FormControl fullWidth>
                                    <InputLabel style={{ backgroundColor: 'transparent' }}>Type</InputLabel>
                                    <Select
                                        value={newJourneyOption.type} 
                                        onChange={(e) => setNewJourneyOption({
                                            ...newJourneyOption,
                                            type: e.target.value,
                                            typeId: "",
                                            typeName: ""
                                        })}
                                        label="Type"
                                        variant="outlined"
                                        required={true}
                                    >
                                        {simulationsEnabled && <MenuItem value={0}>Simulation</MenuItem>}
                                        {coursesEnabled && <MenuItem value={1}>Course</MenuItem>}
                                    </Select>
                                </FormControl>
                            )}

                            {/* Show selection dropdown if at least one option is enabled */}
                            {!noneEnabled && (
                                <FormControl fullWidth>
                                    <InputLabel style={{ backgroundColor: "transparent" }}>
                                        Select {newJourneyOption.type === 1 ? "Course" : "Simulation"}
                                    </InputLabel>
                                    <Select
                                    value={newJourneyOption.type === 0 ? newJourneyOption.typeName : newJourneyOption.typeId}
                                    onChange={(e) => {
                                        if (newJourneyOption.type === 0) {
                                            setNewJourneyOption({
                                                ...newJourneyOption,
                                                typeName: e.target.value,
                                                typeId: ""  
                                            });
                                        } else {
                                            setNewJourneyOption({
                                                ...newJourneyOption,
                                                typeId: e.target.value,
                                                typeName: e.target.value  
                                            });
                                        }
                                    }}
                                    label={"Select " + (newJourneyOption.type === 1 ? "Course" : "Simulation")}
                                    variant="outlined"
                                    required={true}
                                >
                                    {newJourneyOption.type === 1 && courseData && courseData.map((course, index) => (
                                        <MenuItem key={index} value={course.courseName}>
                                            {course.courseName}
                                        </MenuItem>
                                    ))}
                                    {newJourneyOption.type === 0 && simData && simData.map((sim, index) => (
                                        <MenuItem key={index} value={sim.simulationName}>
                                            {sim.simulationName}
                                        </MenuItem>
                                    ))}
                                    </Select>
                                </FormControl>
                            )}

                            {/* Show message if no options are enabled */}
                            {noneEnabled && (
                                <Typography variant="body1" color="text.secondary">
                                    No content types are enabled for Journey Builder. Please contact your administrator.
                                </Typography>
                            )}

                            <TrimmedTextField
                                type="number"
                                label="Month"
                                value={newJourneyOption.month}
                                onChange={(e) => setNewJourneyOption({ ...newJourneyOption, month: e.target.value > 12 ? 12 : e.target.value })}
                                inputProps={{ min: 1, max: 12 }}
                                fullWidth
                                variant="outlined"
                            />
                            <TrimmedTextField
                                type="number"
                                label="Day"
                                value={newJourneyOption.day}
                                onChange={(e) => setNewJourneyOption({ ...newJourneyOption, day: e.target.value > 31 ? 31 : e.target.value })}
                                inputProps={{ min: 1, max: 31 }}
                                fullWidth
                                variant="outlined"
                            />
                            <Button color="warning" variant="contained" size="medium" onClick={handleAddNewJourneyOption} disabled={!isOptionValid() || noneEnabled} sx={{ height: 'fit-content', alignSelf: 'center' }}>
                                Add
                            </Button>
                        </Box>
                    </MainCard>
                </Grid>

                <Grid item xs={6}>
                    <Button color="warning" variant="contained" onClick={handleSave} sx={{ width: '50%', mt: 4 }}>
                        Save Journey
                    </Button>
                </Grid>

                <JourneyTimeline journeyData={journeyData} handleDeleteOption={handleRemoveJourneyOption} />
            </Grid>
        </Grid>
    );
};

export default JourneyEditForm;
